import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { messageLink } from "@shared/lib/jump";
import { ApiError, api, keys } from "@shared/api";
import { useMe } from "@entities/session";
import { Dialog } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { dayTime, time } from "@shared/lib/format";
import { ThreadMenuOn, useChat, useStore } from "@features/chat";
import { insertionAt } from "../lib/reorder";
import { Hint } from "@shared/ui/bits";
import { Menu, MenuOn, SEPARATOR, withId, type MenuItem } from "@shared/ui/Menu";
import { Tile } from "@entities/user";
import { tally } from "@shared/lib/unread";
import type { Category, Channel, FollowedThread, Named, NotifyLevel, ServerState, Uuid } from "@shared/api";
import { plainText } from "@shared/lib/markup";
import type { CallMember } from "@entities/workspace";
import { Input } from "@shared/ui/input";
import { useSlide } from "@shared/motion";
import {
  IconChevronDown,
  IconChevronRight,
  IconMic,
  IconMicOff,
  IconPrivateChannel,
  IconShare,
  IconSoundOff,
  IconTextChannel,
  IconVoiceChannel,
  IconStageChannel,
  IconForumChannel,
  IconAnnouncementChannel,
  IconChat,
} from "@shared/ui/icons";
import { isCall } from "@entities/channel";

/**
 * Где открыть меню «добавить». Ничего — закрыто.
 *
 * Точка нужна потому, что это меню открывают нажатием на кнопку, а не правой
 * кнопкой: у контекстного меню Radix берёт точку из события сам, здесь брать
 * её неоткуда, и без неё меню встало бы в угол экрана.
 */
type At = { x: number; y: number };

/** Куда встанет перетаскиваемый канал. */
interface Drop {
  category: Uuid | null;
  /** Место среди каналов этой категории. */
  index: number;
}

/**
 * Дерево каналов.
 *
 * Канал создаётся из категории, а не из общей кнопки «плюс»: место в дереве
 * выбрано до того, как названо имя. Категория — родитель канала, поэтому
 * «удалить категорию» и «удалить канал» — разные по последствиям действия, и
 * меню говорит об этом до нажатия: категория отпускает свои каналы наверх, а
 * канал уносит сообщения с собой.
 *
 * Три ступени непрочитанного — засечка, светлое имя, счётчик — заложены в
 * разметку с самого начала, ещё когда сервер прочитанное не считал: место под
 * них было занято заранее, и с появлением счётчика строка не переехала.
 */
export function ChannelList({
  state,
  activeChannelId,
  onOpen,
  onOpenChat,
  callMembers,
  peopleById,
  unread,
  mentions,
  threads,
  canManage,
  onCreateChannel,
  onCreateCategory,
  onMarkRead,
  onSettings,
  onChanged,
  voiceMenu,
  onCategoryPermissions,
  hideMuted = false,
  canInvite = false,
  canVoiceStatus = false,
  onInvite,
  voiceInvite,
}: {
  state: ServerState;
  activeChannelId: Uuid | null;
  onOpen: (channel: Channel) => void;
  /** Чат голосового канала — без входа в звонок, как пузырь в Discord. */
  onOpenChat?: (channel: Channel) => void;
  callMembers: Record<Uuid, CallMember[]>;
  peopleById: Map<Uuid, Named>;
  /** Сколько в канале непрочитанного. Канала нет в записи — значит ноль. */
  unread: Record<Uuid, number>;
  /** Сколько веток канала ждут прочтения — из тех, за которыми следим. */
  threads: Record<Uuid, number>;
  /** Сколько из этого адресовано лично. */
  mentions: Record<Uuid, number>;
  /** Есть ли право управлять каналами: без него нет ни меню, ни перетаскивания. */
  canManage: boolean;
  /** Второй довод — тип канала заранее: «Создать голосовой канал» из меню канала. */
  onCreateChannel: (categoryId: Uuid | null, kind?: "text" | "voice") => void;
  /** «Создать категорию» — окном с названием и «приватной», как в Discord. */
  onCreateCategory: () => void;
  /** Пометить канал прочитанным целиком, не открывая. */
  onMarkRead: (channelId: Uuid) => void;
  /** Открыть настройки канала; вторым доводом — сразу с вопросом об удалении. */
  onSettings: (channel: Channel, deleting: boolean) => void;
  /** «Скрыть заглушённые каналы» — как в Discord: каналы с уведомлениями «Ничего» не показываются. */
  hideMuted?: boolean;
  /** Права категории; нет — права управлять правами у человека нет. */
  onCategoryPermissions?: (category: Category) => void;
  onChanged: () => void;
  /**
   * Меню человека под голосовым каналом — то же, что у плитки в звонке. Нет —
   * строка без меню.
   */
  voiceMenu?: (channel: Channel, userId: Uuid) => MenuItem[];
  /** Право приглашать на сервер: без него в меню канала нет «Пригласить». */
  canInvite?: boolean;
  /** Задавать статус голосового канала — своё право, как в Discord. */
  canVoiceStatus?: boolean;
  /** «Пригласить в голосовой чат» / «Пригласить на канал» — окно приглашения в этот канал. */
  onInvite?: (channel: Channel) => void;
  /**
   * Строка «Пригласить в голосовой чат» под своим звонком. Закрыта она или
   * нет, решает вызывающий: список каналов размонтируется, стоит уйти в личку,
   * и пометка «закрыта» жила бы ровно до этого. Нет права приглашать — ничего.
   */
  voiceInvite?: (channel: Channel) => ReactNode;
}) {
  const me = useMe();
  /** Чей статус задают; ничего — окно закрыто. */
  const [statusFor, setStatusFor] = useState<Channel | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsed);
  const {
    notifyChannel,
    notifyServer,
    notifyUntil,
    setChannelNotify,
    unmuteChannel,
    openThread,
    closePanel,
    panel,
    pinnedChannels,
    setChannelPinned,
  } = useStore();
  /** Каналы со скрытыми именами в звонке — «Скрыть имена», помнятся на устройстве. */
  const [hiddenNames, setHiddenNames] = useState<Set<string>>(readHiddenNames);

  /** В каком канале этого сервера я сейчас в звонке. */
  const myCall = me
    ? (Object.keys(callMembers).find((id) => callMembers[id]?.some((member) => member.id === me.id)) ?? null)
    : null;

  /*
   * Подписанные ветки — под своим каналом, как в Discord: пока не затихли.
   * Счётчик веток канала меняется событиями — по нему список и
   * переспрашивается.
   */
  const threadPulse = state.channels.reduce((sum, channel) => sum + (threads[channel.id] ?? 0), 0);
  const { data: followed, refetch: refetchFollowed } = useQuery({
    queryKey: keys.followedThreads(state.id),
    queryFn: () => api.threads.followed(state.id).then((answer) => answer.items),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  // И когда в открытой ветке нажали «Следить» или сняли: строка должна прийти или уйти сразу.
  // И имя: переименованная ветка должна прийти под канал новым именем.
  const openFollowed = useChat((chat) => {
    const open = panel?.threadId ? chat.threads[panel.threadId] : undefined;
    return open ? `${open.followed}:${open.title ?? ""}` : undefined;
  });
  useEffect(() => {
    void refetchFollowed();
  }, [threadPulse, panel?.threadId, openFollowed, refetchFollowed]);
  const threadsOf = (channelId: Uuid) => (followed ?? []).filter((thread) => thread.channel_id === channelId);
  /** Имя ветки под каналом — своё или начало корня без разметки: метка времени — датой, чтобы имя не тикало. */
  const threadName = (thread: FollowedThread) =>
    thread.title ||
    plainText(thread.excerpt ?? "", {
      channelOf: (id) => state.channels.find((item) => item.id === id)?.name ?? null,
    }) ||
    "Ветка";
  const [adding, setAdding] = useState<At | null>(null);
  /** Какую категорию переименовываем прямо в дереве. */
  const [renaming, setRenaming] = useState<Uuid | null>(null);
  const [dragged, setDragged] = useState<Uuid | null>(null);
  const [drop, setDrop] = useState<Drop | null>(null);
  const busy = useRef(false);
  const list = useRef<HTMLDivElement>(null);
  /*
   * Ветка на весь экран — выбрана она, а не канал, как в Discord: линза едет
   * на её строку под каналом, сам канал не подсвечен. Ветка сбоку оставляет
   * выбранным канал — лента его по-прежнему перед глазами.
   */
  const fullThread =
    panel?.kind === "thread" &&
    panel.full &&
    // Ветка из чата голосового стоит сбоку — выбран по-прежнему канал.
    !isCall(state.channels.find((item) => item.id === panel.channelId)?.channel_type ?? "text")
      ? (panel.threadId ?? null)
      : null;
  const lens = useSlide(list, ".chan.active, .chan-thread.active", `${activeChannelId}|${fullThread}`);

  const groups = useMemo(() => group(state.channels, state.categories), [state]);
  /** Закреплённые — в том порядке, в каком стоят в списке. */
  const pinnedList = useMemo(
    () => channelOrder(state.channels, state.categories).filter((channel) => pinnedChannels[channel.id]),
    [state, pinnedChannels],
  );

  /*
   * Совсем пустой сервер — ни канала, ни категории. Только тогда и показываем
   * кнопку добавления.
   *
   * На сервере с каналами она лишняя: те же два действия лежат в меню сервера
   * (кнопка с названием и правая кнопка по ней) и в меню категории — «создать
   * канал здесь». Внизу списка она была третьим входом в то же самое и, в
   * отличие от тех двух, всё время торчала пунктиром под последним каналом.
   *
   * А на пустом сервере входа не остаётся вовсе: меню есть, но искать его в
   * названии — догадка, и первый экран нового сервера выглядел бы сломанным.
   */
  const bare = state.channels.length === 0 && state.categories.length === 0;

  function toggle(key: string) {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeCollapsed(next);
      return next;
    });
  }

  /** «Свернуть все категории» сервера — и обратно, если свёрнуты уже все. */
  const allShut = state.categories.length > 0 && state.categories.every((category) => collapsed.has(category.id));
  function toggleAll() {
    setCollapsed((previous) => {
      const next = new Set(previous);
      for (const category of state.categories) {
        if (allShut) next.delete(category.id);
        else next.add(category.id);
      }
      writeCollapsed(next);
      return next;
    });
  }

  async function rename(id: Uuid, name: string) {
    setRenaming(null);
    const trimmed = name.trim();
    if (!trimmed) return;

    await api.categories.update(id, { name: trimmed });
    onChanged();
  }

  /**
   * Раскладывает каналы заново после перетаскивания.
   *
   * Правится не только перенесённый: порядок — это номера у всех соседей, и
   * пропущенный сосед остался бы с чужим номером.
   */
  async function land(channelId: Uuid, target: Drop) {
    if (busy.current) return;
    busy.current = true;

    try {
      const moved = state.channels.find((item) => item.id === channelId);
      if (!moved) return;

      /*
       * Порядок на экране — вместе с перетаскиваемым, а вставляем в список без
       * него. Пересчёт черты в место вставки живёт в `lib/reorder`: там же он и
       * проверен, потому что ошибался он молча и только в одну сторону.
       */
      const order = state.channels
        .filter((item) => (item.category_id ?? null) === target.category)
        .sort((a, b) => a.position - b.position)
        .map((item) => item.id);

      const siblings = state.channels
        .filter((item) => (item.category_id ?? null) === target.category && item.id !== channelId)
        .sort((a, b) => a.position - b.position);

      siblings.splice(insertionAt(order, channelId, target.index), 0, moved);

      for (const [index, item] of siblings.entries()) {
        const sameCategory = (item.category_id ?? null) === target.category;
        if (item.position === index && sameCategory) continue;

        await api.channels.update(item.id, {
          position: index,
          ...(sameCategory ? {} : { category_id: target.category }),
        });
      }

      onChanged();
    } finally {
      busy.current = false;
      setDragged(null);
      setDrop(null);
    }
  }

  async function removeCategory(id: Uuid) {
    await api.categories.remove(id);
    onChanged();
  }

  /*
   * Дубль канала — как «Клонировать канал» в Discord: то же имя, тип, тема и
   * категория, и те же записи прав поверх ролей. Ложится в конец категории.
   */
  async function duplicate(channel: Channel) {
    const copy = await api.channels.create(state.id, {
      name: channel.name,
      channel_type:
        channel.channel_type === "voice" ||
        channel.channel_type === "stage" ||
        channel.channel_type === "forum" ||
        channel.channel_type === "announcement"
          ? channel.channel_type
          : "text",
      category_id: channel.category_id ?? null,
      topic: channel.topic ?? null,
    });
    const rights = await api.roles.channelOverrides(channel.id).catch(() => null);
    if (rights) {
      await api.roles.setChannelDefault(copy.id, rights.default);
      for (const role of rights.roles) await api.roles.setChannelRole(copy.id, role.role_id, role.permissions);
      // Записи на участников — тоже: «Клонировать канал» в Discord копирует все права.
      for (const member of rights.members ?? []) {
        await api.roles.setChannelMember(copy.id, member.user_id, member.permissions).catch(() => {});
      }
    }
    onChanged();
  }

  /** Пункты меню канала. Один набор на оба пути: правую кнопку и кнопку «…». */
  function itemsForChannel(channel: Channel): MenuItem[] {
    // Статус голосового канала задаёт тот, кто в звонке, — как в Discord.
    const inThisCall = myCall === channel.id;
    return channelItems({
      channel,
      canManage,
      canInvite: canInvite && !!onInvite,
      canStatus: canVoiceStatus,
      inCall: inThisCall,
      pinned: !!pinnedChannels[channel.id],
      namesHidden: hiddenNames.has(channel.id),
      level: notifyChannel[channel.id],
      mutedUntil: notifyUntil[channel.id],
      serverLevel: notifyServer[state.id] ?? state.default_notify ?? "all",
      onRead: () => onMarkRead(channel.id),
      onInvite: () => onInvite?.(channel),
      onPin: () => void setChannelPinned(channel.id, !pinnedChannels[channel.id]).catch(() => {}),
      onCopyLink: () =>
        void navigator.clipboard
          ?.writeText(messageLink({ serverId: state.id, channelId: channel.id, messageId: null }))
          .catch(() => {}),
      onOpenChat: onOpenChat ? () => onOpenChat(channel) : undefined,
      onSetStatus: () => setStatusFor(channel),
      onClearStatus: () => void api.voice.setStatus(channel.id, null).then(onChanged, () => {}),
      onHideNames: () =>
        setHiddenNames((previous) => {
          const next = new Set(previous);
          if (!next.delete(channel.id)) next.add(channel.id);
          writeHiddenNames(next);
          return next;
        }),
      onMute: (minutes) => void setChannelNotify(channel.id, "none", minutes),
      onNotify: (level) => void setChannelNotify(channel.id, level),
      onUnmute: () => void unmuteChannel(channel.id).catch(() => {}),
      moveTo: [
        {
          label: "вне категорий",
          onPick: () => void land(channel.id, { category: null, index: 0 }),
        },
        ...state.categories.map((category) => ({
          label: category.name,
          onPick: () => void land(channel.id, { category: category.id, index: 0 }),
        })),
      ],
      onSettings: () => onSettings(channel, false),
      onDuplicate: () => void duplicate(channel),
      onCreate: (kind) => onCreateChannel(channel.category_id ?? null, kind),
      onDelete: () => onSettings(channel, true),
    });
  }

  /**
   * Строка канала со всем, что под ней. Та же строка встаёт и в «Закреплённые
   * каналы» сверху (`place.pinned`) — там без веток и состава звонка: они
   * остаются под каналом на его месте, как в Discord.
   */
  function channelRow(channel: Channel, place: { index: number; categoryId: Uuid | null; pinned?: boolean }) {
    const { index, categoryId } = place;
    const inCall = callMembers[channel.id] ?? [];
    const active = channel.id === activeChannelId && !(fullThread && panel?.channelId === channel.id);
    /*
     * Засечка есть и у открытого канала.
     *
     * Счёт ведётся по увиденному, а не по открытому: у канала
     * перед глазами непрочитанное остаётся ровно тогда, когда его
     * конец за нижним краем. Прятать число в этом случае значит
     * молчать именно в тот момент, когда оно и нужно.
     */
    const fresh = (unread[channel.id] ?? 0) > 0;
    const marked = !place.pinned && drop?.category === categoryId && drop.index === index;
    const namesHidden = hiddenNames.has(channel.id);

    return (
      <div key={place.pinned ? `pinned-${channel.id}` : channel.id}>
        {marked && <div className="drop-line" />}

        <MenuOn
          title={`#${channel.name.toUpperCase()}`}
          items={withId(itemsForChannel(channel), channel.id)}
        >
        {/*
          Перетаскивание и опора контекстного меню сидят на одном
          узле, и это проверено на живом приложении.

          У Radix есть известный конфликт этих двух вещей
          (radix-ui/primitives#2867), и одно время строка была
          обёрнута ради их разведения. Но причиной поломки он не
          был: события забирал `dragDropEnabled` в настройках окна
          Tauri. Обёртку сняли — перетаскивание живо.
        */}
        <div
          className="chan-row"
          // Со скрытыми каналами порядок видимых не совпадает с настоящим —
          // перетаскивание тогда выключено.
          draggable={canManage && !hideMuted && !place.pinned}
          onDragStart={() => setDragged(channel.id)}
          onDragEnd={() => {
            setDragged(null);
            setDrop(null);
          }}
          onDragOver={(event) => {
            if (!dragged || dragged === channel.id) return;
            event.preventDefault();
            const box = event.currentTarget.getBoundingClientRect();
            const after = event.clientY > box.top + box.height / 2;
            setDrop({ category: categoryId, index: index + (after ? 1 : 0) });
          }}
          onDrop={(event) => {
            if (!dragged || !drop) return;
            event.preventDefault();
            void land(dragged, drop);
          }}
        >
          {/*
            Справа от голосового канала — кнопка его чата: подсказка
            с именем встаёт за ней, а не поверх.
          */}
          <Hint
            label={channel.name}
            side="right"
            offset={isCall(channel.channel_type) && onOpenChat ? 38 : undefined}
          >
            <button
              type="button"
              className={`chan ${active ? "active" : ""} ${fresh ? "unread" : ""} ${
                dragged === channel.id ? "dragging" : ""
              }`}
              onClick={() => {
                // Из ветки на весь экран — обратно в её канал, как в Discord: канал уже
                // открыт, и без этого щелчок по нему ничего бы не менял.
                if (fullThread && panel?.channelId === channel.id) closePanel();
                onOpen(channel);
              }}
            >
              {fresh && <span className="chan-notch" />}
              {/* Закрытый канал — как в Discord: знак типа остаётся, а замок
                  садится ему в правый верхний угол. */}
              <span className={`chan-sigil${channel.private ? " private" : ""}`}>
                {channel.channel_type === "stage" ? (
                  <IconStageChannel size={14} />
                ) : isCall(channel.channel_type) ? (
                  <IconVoiceChannel size={14} />
                ) : channel.channel_type === "forum" ? (
                  <IconForumChannel size={14} />
                ) : channel.channel_type === "announcement" ? (
                  <IconAnnouncementChannel size={14} />
                ) : (
                  <IconTextChannel size={14} />
                )}
                {channel.private && (
                  <span className="chan-lock">
                    <IconPrivateChannel size={9} strokeWidth={2.6} />
                  </span>
                )}
              </span>
              <span className="chan-name">{channel.name}</span>
              {/* Лимит — как в Discord: «сколько внутри / сколько пустит». */}
              {isCall(channel.channel_type) && (channel.user_limit ?? 0) > 0 && (
                <span
                  className={`chan-limit${inCall.length >= (channel.user_limit ?? 0) ? " full" : ""}`}
                  aria-label={`${inCall.length} из ${channel.user_limit}`}
                >
                  {String(inCall.length).padStart(2, "0")}/{String(channel.user_limit).padStart(2, "0")}
                </span>
              )}
              {/*
                Число — сколько нового, собачка впереди — что среди
                него есть адресованное вам. Кружок при этом акцентом:
                в макете акцент и значит «вас позвали».
              */}
              {/*
                Ветки — свой знак в том же слоте: обведённый счётчик.
                Заполненный значит «позвали», обведённый — «новое в
                ветках». Два знака рядом, третьего не бывает.
              */}
              {(threads[channel.id] ?? 0) > 0 && (
                <span className="badge thread">{threads[channel.id]}</span>
              )}
              {fresh ? (
                <span className={`badge ${mentions[channel.id] ? "" : "quiet"}`}>
                  {tally(unread[channel.id] ?? 0, mentions[channel.id] ?? 0)}
                </span>
              ) : (
                <span className="badge-slot" />
              )}
            </button>
          </Hint>
          {isCall(channel.channel_type) && onOpenChat && (
            <button
              type="button"
              className={`chan-chat ${fresh ? "fresh" : ""}`}
              title="Открыть чат"
              aria-label={`Открыть чат канала ${channel.name}`}
              onClick={() => onOpenChat(channel)}
            >
              <IconChat size={13} />
            </button>
          )}
        </div>
        </MenuOn>

        {isCall(channel.channel_type) && channel.voice_status && (
          <div className="chan-voice-status ellipsis" title={channel.voice_status}>
            {channel.voice_status}
          </div>
        )}

        {/*
          Ветка под каналом — как в Discord: щелчок открывает её на
          месте ленты, правая кнопка — то же меню ветки, что везде.
        */}
        {!place.pinned && threadsOf(channel.id).map((thread) => (
          <ThreadMenuOn
            key={thread.id}
            thread={{
              channelId: channel.id,
              threadId: thread.id,
              rootId: thread.root_message_id ?? null,
              ownerId: thread.owner_id,
              // Под каналом стоят только подписанные.
              followed: true,
              notifyLevel: thread.notify_level ?? null,
              notifyUntil: thread.notify_until,
              name: thread.title,
              closed: thread.closed,
              locked: thread.locked,
              unread: thread.unread,
            }}
          >
            <button
              type="button"
              className={`chan-thread ${fullThread === thread.id ? "active" : ""} ${
                thread.unread > 0 ? "unread" : ""
              }`}
              title={threadName(thread)}
              onClick={() => {
                onOpen(channel);
                if (thread.root_message_id) openThread(channel.id, thread.root_message_id, thread.id, true);
              }}
            >
              <span className="chan-thread-spine" aria-hidden />
              <span className="chan-name ellipsis">{threadName(thread)}</span>
              {thread.mentions > 0 && <span className="badge">{thread.mentions}</span>}
            </button>
          </ThreadMenuOn>
        ))}

        {!place.pinned && inCall.length > 0 && (
          <div className="voice-live">
            {/*
              «Скрыть имена» — как в Discord: лица в ряд, не больше шести, остальные
              числом.
            */}
            {namesHidden && (
              <div className="voice-faces">
                {(inCall.length > FACES + 1 ? inCall.slice(0, FACES) : inCall).map((member) => {
                  const user = peopleById.get(member.id);
                  return (
                    <Tile
                      key={member.id}
                      person={{
                        id: member.id,
                        name: user?.display_name || user?.username || "…",
                        handle: user?.handle ?? member.id,
                        avatarId: user?.avatar_id ?? null,
                        presence: null,
                        online: true,
                      }}
                      size={24}
                      mark={null}
                    />
                  );
                })}
                {inCall.length > FACES + 1 && <span className="voice-faces-more">+{inCall.length - FACES}</span>}
              </div>
            )}
            {!namesHidden && inCall.map((member) => {
              const user = peopleById.get(member.id);
              return (
                <MenuOn
                  key={member.id}
                  title={`УЧАСТНИК · ${(user?.display_name || user?.username || "участник").toUpperCase()}`}
                  items={withId(voiceMenu?.(channel, member.id) ?? [], member.id)}
                  disabled={!voiceMenu}
                >
                <div className="voice-live-row">
                  <Tile
                    person={{
                      id: member.id,
                      name: user?.display_name || user?.username || "…",
                      handle: user?.handle ?? member.id,
                      avatarId: user?.avatar_id ?? null,
                      presence: null,
                      online: true,
                    }}
                    size={24}
                    mark={null}
                  />
                  <span className="ellipsis grow">
                    {user?.display_name || user?.username || "участник"}
                  </span>
                  <VoiceFlags member={member} />
                </div>
                </MenuOn>
              );
            })}
            {/*
              «Пригласить в голосовой чат» — под своим звонком, как в Discord;
              в свёрнутой категории и со скрытыми именами её нет.
            */}
            {voiceInvite &&
              channel.id === myCall &&
              !namesHidden &&
              !collapsed.has(channel.category_id ?? "loose") &&
              voiceInvite(channel)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="chan-list" ref={list}>
      {/*
        Подсветка выбранного канала одна на весь список и едет к новому
        выбору, по пути вытягиваясь: край в сторону движения уходит сразу,
        дальний догоняет с задержкой — так линза скользит в макете v6.
      */}
      {lens && (
        <span
          className="chan-lens"
          style={{
            top: lens.top,
            bottom: lens.bottom,
            // На строке ветки линза начинается после изгиба — там, где сама строка.
            left: fullThread ? "var(--thread-indent)" : 0,
            transition: !lens.moved
              ? "none"
              : lens.down
                ? "top 0.66s var(--ease-spring) 0.07s, bottom 0.5s var(--ease-spring), left 0.5s var(--ease-spring)"
                : "top 0.5s var(--ease-spring), bottom 0.66s var(--ease-spring) 0.07s, left 0.5s var(--ease-spring)",
          }}
        />
      )}
      {/*
        «Закреплённые каналы» — свои закреплённые вверху списка, как в Discord;
        на своём месте в категории канал остаётся.
      */}
      {pinnedList.length > 0 && (
        <div key="pinned">
          <div className="cat-row">
            <button type="button" className="cat" onClick={() => toggle(PINNED_KEY)}>
              {collapsed.has(PINNED_KEY) ? <IconChevronRight size={12} /> : <IconChevronDown size={12} />}
              Закреплённые каналы
            </button>
          </div>
          {(collapsed.has(PINNED_KEY)
            ? pinnedList.filter((channel) => channel.id === activeChannelId)
            : pinnedList
          ).map((channel, index) => channelRow(channel, { index, categoryId: null, pinned: true }))}
        </div>
      )}
      {groups.map(({ category, channels: all }) => {
        // Открытый и тот, где вас позвали, остаются и заглушёнными: прятать их
        // значило бы прятать то, ради чего человек сюда пришёл.
        const channels = hideMuted
          ? all.filter(
              (channel) =>
                notifyChannel[channel.id] !== "none" ||
                channel.id === activeChannelId ||
                (mentions[channel.id] ?? 0) > 0,
            )
          : all;
        const key = category?.id ?? "loose";
        const shut = collapsed.has(key);
        const categoryId = category?.id ?? null;

        return (
          <div key={key}>
            {category && (
              <MenuOn
                title={`КАТЕГОРИЯ · ${category.name.toUpperCase()}`}
                items={categoryItems({
                  id: category.id,
                  shut,
                  canManage,
                  onRead: () => channels.forEach((channel) => onMarkRead(channel.id)),
                  onCreateChannel,
                  onAddCategory: onCreateCategory,
                  onRename: () => setRenaming(category.id),
                  onCollapse: () => toggle(key),
                  allShut,
                  onCollapseAll: toggleAll,
                  onDelete: () => void removeCategory(category.id),
                  onPermissions: onCategoryPermissions ? () => onCategoryPermissions(category) : undefined,
                })}
              >
              <div
                className={`cat-row ${drop?.category === categoryId && drop.index === 0 ? "over" : ""}`}
                onDragOver={(event) => {
                  if (!dragged) return;
                  event.preventDefault();
                  setDrop({ category: categoryId, index: 0 });
                }}
                onDrop={(event) => {
                  if (!dragged) return;
                  event.preventDefault();
                  void land(dragged, { category: categoryId, index: 0 });
                }}
              >
                {renaming === category.id ? (
                  <Input
                    className="cat-input"
                    defaultValue={category.name}
                    autoFocus
                    onBlur={(event) => void rename(category.id, event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.currentTarget.blur();
                      if (event.key === "Escape") setRenaming(null);
                    }}
                  />
                ) : (
                  <>
                    {/*
                      Подсказка на самой кнопке, а не вокруг строки: строкой
                      уже владеет меню по правой кнопке, и его опора должна
                      доставать до узла. Две опоры на одном узле — это две
                      подмены `asChild` подряд, и одна из них теряется.
                    */}
                    <Hint label={category.name} side="right">
                      <button type="button" className="cat" onClick={() => toggle(key)}>
                        {shut ? <IconChevronRight size={12} /> : <IconChevronDown size={12} />}
                        {category.name}
                      </button>
                    </Hint>
                  </>
                )}
              </div>
              </MenuOn>
            )}

            {/*
              Свёрнутая категория держит на виду открытый канал и голосовые, где
              сидят люди, — как в Discord.
            */}
            {(shut
              ? channels.filter(
                  (channel) => channel.id === activeChannelId || (callMembers[channel.id]?.length ?? 0) > 0,
                )
              : channels
            ).map((channel) => channelRow(channel, { index: channels.indexOf(channel), categoryId }))}

            {/* Хвост категории: сюда падает канал, брошенный ниже последнего. */}
            {!shut && dragged && (
              <div
                className={`drop-tail ${
                  drop?.category === categoryId && drop.index >= channels.length ? "over" : ""
                }`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDrop({ category: categoryId, index: channels.length });
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  void land(dragged, { category: categoryId, index: channels.length });
                }}
              />
            )}
          </div>
        );
      })}

      {canManage && bare && (
        <button
          type="button"
          className="chan-add"
          onClick={(event) => setAdding({ x: event.clientX, y: event.clientY })}
        >
          + канал или категория
        </button>
      )}

      {adding && (
        <Menu
          title="ДОБАВИТЬ"
          at={adding}
          onClose={() => setAdding(null)}
          items={[
            {
              label: "Создать канал",
              hint: "вне категорий",
              onPick: () => onCreateChannel(null),
            },
            { label: "Создать категорию", onPick: onCreateCategory },
          ]}
        />
      )}
      {statusFor && (
        <VoiceStatusDialog channel={statusFor} onClose={() => setStatusFor(null)} onSaved={onChanged} />
      )}
    </div>
  );
}

/**
 * Что человек делает в звонке: значками, справа от имени.
 *
 * Микрофон показывается всегда, в двух положениях, а звук и показ — только
 * когда есть что сказать. Причина в разнице вопросов: «слышно ли его» —
 * первое, что хотят знать про сидящего в канале, и отвечать на него молчанием
 * нельзя. «Оглушён» и «показывает» — состояния редкие, и постоянный значок
 * под каждым именем в узкой колонке был бы шумом.
 *
 * Признаки те же, что рисуются поверх плашки в самом звонке, и знаки у них
 * те же: один и тот же человек не должен выглядеть по-разному в списке и в
 * канале.
 */
function VoiceFlags({ member }: { member: CallMember }) {
  return (
    <span className="voice-live-flags">
      <span className={member.microphone ? "" : "bad"}>
        {member.microphone ? <IconMic size={14} /> : <IconMicOff size={14} />}
      </span>
      {member.deafened && (
        <span className="bad">
          <IconSoundOff size={14} />
        </span>
      )}
      {member.live && (
        <span className="on">
          <IconShare size={14} />
        </span>
      )}
    </span>
  );
}

/**
 * Меню категории.
 *
 * «Удалить категорию» стоит за разделителем, но подтверждения именем не
 * просит: каналы не пропадают, они поднимаются на верхний уровень, и действие
 * откатывается руками. Подтверждение именем остаётся там, где данные
 * исчезают, — у канала и у сервера.
 */
function categoryItems({
  id,
  shut,
  canManage,
  onRead,
  onCreateChannel,
  onAddCategory,
  onRename,
  onCollapse,
  allShut,
  onCollapseAll,
  onDelete,
  onPermissions,
}: {
  id: Uuid;
  shut: boolean;
  /** Без права управлять — как у всех в Discord: прочитать и свернуть. */
  canManage: boolean;
  onRead: () => void;
  onCreateChannel: (categoryId: Uuid | null) => void;
  onAddCategory: () => void;
  onRename: () => void;
  onCollapse: () => void;
  allShut: boolean;
  onCollapseAll: () => void;
  onDelete: () => void;
  /** Нет — права категории не открыть: у человека нет права управлять правами. */
  onPermissions?: () => void;
}): MenuItem[] {
  const common: MenuItem[] = [
    { label: "Пометить прочитанной", onPick: onRead },
    { label: shut ? "Развернуть категорию" : "Свернуть категорию", onPick: onCollapse },
    { label: allShut ? "Развернуть все категории" : "Свернуть все категории", onPick: onCollapseAll },
  ];
  if (!canManage) return common;
  return [
    ...common,
    SEPARATOR,
    { label: "Создать канал", hint: "здесь", onPick: () => onCreateChannel(id) },
    { label: "Создать категорию", onPick: onAddCategory },
    { label: "Переименовать", onPick: onRename },
    // Права категории повторяют синхронизированные с ней каналы — как в Discord.
    ...(onPermissions ? [{ label: "Права категории", onPick: onPermissions }] : []),
    SEPARATOR,
    { label: "Удалить категорию", kind: "danger", onPick: onDelete },
  ];
}

/** Свёрнутые категории помнятся между запусками, как в Discord. Ключи — id категорий. */
const COLLAPSED_KEY = "airel.collapsed-categories";
/** Каналы со скрытыми именами в звонке — тоже на устройстве. */
const HIDDEN_NAMES_KEY = "airel.voice-names-hidden";
/** Ключ группы «Закреплённые каналы» среди свёрнутых. */
const PINNED_KEY = "pinned";
/** Сколько лиц в ряду со скрытыми именами — как в Discord, остальные числом. */
const FACES = 6;

function readHiddenNames(): Set<string> {
  try {
    const list: unknown = JSON.parse(localStorage.getItem(HIDDEN_NAMES_KEY) ?? "[]");
    return new Set(Array.isArray(list) ? list.filter((item): item is string => typeof item === "string") : []);
  } catch {
    return new Set();
  }
}

function writeHiddenNames(hidden: Set<string>) {
  try {
    localStorage.setItem(HIDDEN_NAMES_KEY, JSON.stringify([...hidden].slice(-500)));
  } catch {
    // Хранилище закрыто — скрытое просто не переживёт перезапуск.
  }
}

function readCollapsed(): Set<string> {
  try {
    const list: unknown = JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? "[]");
    return new Set(Array.isArray(list) ? list.filter((item): item is string => typeof item === "string") : []);
  } catch {
    return new Set();
  }
}

function writeCollapsed(collapsed: Set<string>) {
  try {
    // Удалённые категории в списке остаются — пусть не копятся без конца.
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed].slice(-500)));
  } catch {
    // Хранилище закрыто — свёрнутое просто не переживёт перезапуск.
  }
}

const LEVELS: { level: NotifyLevel; label: string }[] = [
  { level: "all", label: "Все сообщения" },
  { level: "mentions", label: "Только @упоминания" },
  { level: "none", label: "Ничего" },
];

/** Подпись уровня — справа у пункта «Уведомления», как текущее значение. */
export function levelLabel(level: NotifyLevel): string {
  return LEVELS.find((item) => item.level === level)?.label ?? "";
}

/**
 * Меню канала.
 *
 * Переименования здесь нет намеренно: имя канала — это адрес, и менять его
 * надо там, где видно, во что превратится набранное. Оно живёт в настройках,
 * рядом с той же подсказкой, что и при создании.
 */
/** «Заглушить канал» — сроки как в Discord; `null` — пока не включу. */
const MUTE_FOR: { label: string; minutes: number | null }[] = [
  { label: "На 15 минут", minutes: 15 },
  { label: "На 1 час", minutes: 60 },
  { label: "На 3 часа", minutes: 180 },
  { label: "На 8 часов", minutes: 480 },
  { label: "На 24 часа", minutes: 1440 },
  { label: "Пока не включу", minutes: null },
];

/** «до 18:30» сегодня, «до 30 октября, 9:00» — позже. */
function mutedLabel(until: string | undefined): string {
  if (!until) return "заглушён";
  const at = new Date(until);
  return at.toDateString() === new Date().toDateString() ? `до ${time(until)}` : `до ${dayTime(until)}`;
}

/**
 * Меню канала — как в Discord (меню канала в списке, голосового и
 * текстового): те же группы в том же порядке, чего нет по праву — нет вовсе.
 *
 * - прочитано; «Добавить в избранное» (у нас пока планом);
 * - пригласить, закрепить вверху, ссылка;
 * - у голосового: открыть чат, статус канала, «Скрыть имена»;
 * - заглушить и уведомления — у голосового в Discord уведомлений нет, у сцены
 *   их три;
 * - переместить;
 * - настроить, клонировать, создать такой же канал, удалить.
 */
function channelItems({
  channel,
  canManage,
  canInvite,
  canStatus,
  inCall,
  pinned,
  namesHidden,
  level,
  mutedUntil,
  serverLevel,
  onRead,
  onInvite,
  onPin,
  onCopyLink,
  onOpenChat,
  onSetStatus,
  onClearStatus,
  onHideNames,
  onMute,
  onNotify,
  onUnmute,
  moveTo,
  onSettings,
  onDuplicate,
  onCreate,
  onDelete,
}: {
  channel: Channel;
  /** Без права управлять — как у всех в Discord: без перемещения, настроек и удаления. */
  canManage: boolean;
  canInvite: boolean;
  canStatus: boolean;
  /** Я в звонке этого канала: статус задаёт тот, кто внутри. */
  inCall: boolean;
  pinned: boolean;
  namesHidden: boolean;
  /** Свой уровень канала; нет — как у сервера. */
  level: NotifyLevel | undefined;
  /** Заглушён на время — до какого. */
  mutedUntil?: string;
  serverLevel: NotifyLevel;
  onRead: () => void;
  onInvite: () => void;
  onPin: () => void;
  onCopyLink: () => void;
  /** Чат голосового канала; нет — пункта нет. */
  onOpenChat?: () => void;
  onSetStatus: () => void;
  onClearStatus: () => void;
  onHideNames: () => void;
  /** «Заглушить канал» на столько минут; `null` — пока не включу. */
  onMute: (minutes: number | null) => void;
  onNotify: (level: NotifyLevel | null) => void;
  onUnmute: () => void;
  /** Куда можно переместить: вложенное меню, а не второе меню поверх. */
  moveTo: MenuItem[];
  onSettings: () => void;
  onDuplicate: () => void;
  onCreate: (kind: "text" | "voice") => void;
  onDelete: () => void;
}): MenuItem[] {
  const call = isCall(channel.channel_type);
  const voice = channel.channel_type === "voice";
  // Уровни Discord: у голосового их нет вовсе, у сцены — без «Все сообщения».
  const levels = voice ? [] : channel.channel_type === "stage" ? LEVELS.filter((item) => item.level !== "all") : LEVELS;
  // «Создать такой же» — как в Discord: только у текстового и голосового.
  const sameKind = channel.channel_type === "text" ? "text" : voice ? "voice" : null;

  const groups: (MenuItem | false)[][] = [
    [{ label: "Пометить как прочитанное", onPick: onRead }],
    [{ label: "Добавить в избранное", kind: "plan" }],
    [
      canInvite && { label: call ? "Пригласить в голосовой чат" : "Пригласить на канал", onPick: onInvite },
      { label: pinned ? "Открепить канал" : "Закрепить канал вверху", onPick: onPin },
      { label: "Копировать ссылку", onPick: onCopyLink },
    ],
    call
      ? [
          !!onOpenChat && { label: "Открыть чат", onPick: onOpenChat },
          // Статус — у голосового, не у сцены. Вне звонка его может только снять тот, кто управляет каналом.
          voice && inCall && canStatus && { label: "Установить статус канала", onPick: onSetStatus },
          voice && !inCall && canManage && !!channel.voice_status && {
            label: "Очистить статус канала",
            onPick: onClearStatus,
          },
          { label: "Скрыть имена", toggle: true, on: namesHidden, onPick: onHideNames },
        ]
      : [],
    [
      level === "none"
        ? { label: "Включить уведомления", hint: mutedLabel(mutedUntil), onPick: onUnmute }
        : {
            label: "Заглушить канал",
            items: MUTE_FOR.map((item) => ({ label: item.label, onPick: () => onMute(item.minutes) })),
          },
      levels.length > 0 && {
        label: "Параметры уведомлений",
        hint: levelLabel(level ?? serverLevel),
        items: [
          {
            label: `Как у сервера · ${levelLabel(serverLevel).toLowerCase()}`,
            choice: true,
            on: level === undefined,
            onPick: () => onNotify(null),
          },
          ...levels.map((item) => ({
            label: item.label,
            choice: true,
            on: level === item.level,
            onPick: () => onNotify(item.level),
          })),
        ],
      },
    ],
    canManage ? [{ label: "Переместить…", items: moveTo }] : [],
    canManage
      ? [
          { label: "Настроить канал", onPick: onSettings },
          { label: "Клонировать канал", onPick: onDuplicate },
          !!sameKind && {
            label: sameKind === "voice" ? "Создать голосовой канал" : "Создать текстовый канал",
            onPick: () => onCreate(sameKind),
          },
          { label: "Удалить канал", hint: "спросит имя", kind: "danger", onPick: onDelete },
        ]
      : [],
  ];

  return groups
    .map((items) => items.filter((item): item is MenuItem => !!item))
    .filter((items) => items.length > 0)
    .flatMap((items, index) => (index === 0 ? items : [SEPARATOR, ...items]));
}

/**
 * Каналы по категориям.
 *
 * Порядок внутри категории — тот, что задан перетаскиванием, а не имя и не
 * время создания: дерево человек выстраивает сам.
 */
function group(
  channels: Channel[],
  categories: Category[],
): { category: Category | null; channels: Channel[] }[] {
  const loose = channels.filter((channel) => !channel.category_id);
  const result: { category: Category | null; channels: Channel[] }[] = [];

  if (loose.length) result.push({ category: null, channels: byPosition(loose) });

  for (const category of [...categories].sort((a, b) => a.position - b.position)) {
    result.push({
      category,
      channels: byPosition(channels.filter((channel) => channel.category_id === category.id)),
    });
  }

  return result;
}

/** Каналы подряд — в том порядке, в каком они стоят в списке: по нему ходят Alt+↑/↓. */
export function channelOrder(channels: Channel[], categories: Category[]): Channel[] {
  return group(channels, categories).flatMap((part) => part.channels);
}

function byPosition(channels: Channel[]): Channel[] {
  return [...channels].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, "ru"));
}

/** Статус голосового канала — короткая строка «чем заняты», до 100 знаков. */
function VoiceStatusDialog({
  channel,
  onClose,
  onSaved,
}: {
  channel: Channel;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState(channel.voice_status ?? "");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function save(next: string | null) {
    setBusy(true);
    setProblem(null);
    try {
      await api.voice.setStatus(channel.id, next);
      onSaved();
      onClose();
    } catch (error) {
      setProblem(
        error instanceof ApiError && error.code === "automod"
          ? "АвтоМод сервера не пропустил слово"
          : error instanceof ApiError
            ? error.message
            : "Не сохранилось",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Статус канала"
      onClose={onClose}
      actions={
        <>
          {channel.voice_status && (
            <Button type="button" variant="danger" disabled={busy} onClick={() => void save(null)}>
              Снять
            </Button>
          )}
          <Button type="button" disabled={busy || !status.trim()} onClick={() => void save(status.trim())}>
            Сохранить
          </Button>
        </>
      }
    >
      <Input
        value={status}
        maxLength={100}
        placeholder="Чем заняты? Например, «играем в шахматы»"
        aria-label="Статус канала"
        onChange={(event) => setStatus(event.target.value)}
      />
      <div className="small muted" style={{ marginTop: 8 }}>
        Виден всем под именем канала и уходит, когда из звонка выйдет последний.
      </div>
      {problem && <div className="small danger-text">{problem}</div>}
    </Dialog>
  );
}
